import * as React from "react";
import * as ReactDOM from "react-dom";
import { connect } from "react-redux";
import get from "lodash/get";
import mapValues from "lodash/mapValues";
import pick from "lodash/pick";
import pickBy from "lodash/pickBy";
import set from "lodash/set";

import { isEditMode } from "../../utils/sendClickToMerchant";
import { validate } from "../../utils/validators";
import {
  accountCreditRedeem as accountCreditRedeemAction,
  AccountCreditRedeemActionParam,
  clearWidgetFormErrors,
  disableWidgetConfig,
  emailShare,
  getLedgerContext,
  getPDPPointsTotal,
  getReferral,
  GetReferralParams,
  getShare,
  loyaltyOptIn,
  pollForReward,
  setScreenName,
  setUserInput,
  setWidgetFormError,
  trackEvent,
  widgetEventTrack,
} from "../../actions";
import { redirect } from "../../components/actions/redirect";
import { copyText } from "../../components/actions/copyText";
import { getEmailsFromString, interpolate } from "../../utils/string";
import { extractShopifyParams } from "../../utils/shopify";
import { extractInfluencerAndAdvocate } from "../../utils/share";
import { StoreState } from "../../reducers";
import {
  getAccountCreditRedeem,
  getCampaignId,
  getCoupon,
  getMerchantId,
  getReferralRequestStatus,
  getScreenName,
  getWidgetConfigId,
  getWidgetData,
  getWidgetFormErrors,
  getWidgetId,
} from "../../reducers/methods";
import { getAllChildren } from "../../utils/getAllChildren";
import { WIDGET_URL } from "../../config";
import "./SuccessNotification.css";
import {
  customerEmailChanged,
  customerIsAuthenticated,
} from "../../utils/customer";
import { ActionOnLoadArgs, IndexScreen } from "../../types";
import {
  APPLE_WALLET_CHANNEL,
  APPLE_WALLET_CLICK_EVENT_NAME,
  GOOGLE_WALLET_CHANNEL,
  GOOGLE_WALLET_CLICK_EVENT_NAME,
  PRODUCT_CHANNELS,
  PRODUCT_PURL_CHANNEL,
} from "../../constants";

export interface ActionsProps {
  accountCreditRedeem: ReturnType<typeof getAccountCreditRedeem>;
  data: any;
  referral: { [index: string]: any };
  config: { [index: string]: any };
  merchantId: string;
  campaignId: string;
  coupon: ReturnType<typeof getCoupon>;
  validateFields: string[];
  requestStatus: string;
  widgetConfigId: string;
  widgetId: string;
  screenName: string;
  formErrors: {
    [key: string]: { [index: string]: any };
  };
  setWidgetFormError: typeof setWidgetFormError;
  setScreenName: typeof setScreenName;

  // widget configurable actions from props
  // some are used in different manner like getShare (different share channel)
  accountCreditRedeemAction: typeof accountCreditRedeemAction;
  disableWidgetConfig: typeof disableWidgetConfig;
  emailShare: typeof emailShare;
  getLedgerContext: typeof getLedgerContext;
  getPDPPointsTotal: typeof getPDPPointsTotal;
  getReferral: typeof getReferral;
  getShare: typeof getShare;
  loyaltyOptIn: typeof loyaltyOptIn;
  widgetEventTrack: typeof widgetEventTrack;
  pollForReward: typeof pollForReward;
  setUserInput: typeof setUserInput;
  trackEvent: typeof trackEvent;
  clearWidgetFormErrors: typeof clearWidgetFormErrors;

  [otherProps: string]: any;
}

const availablePropActions = [
  "emailShare",
  "getReferral",
  "getShare",
  "getLedgerContext",
  "loyaltyOptIn",
  "copyText",
  "disableWidgetConfig",
  "redirect",
  "transitionScreen",
  "trackEvent",
  "getPDPPointsTotal",
  "productShare",
];

const asyncActions = ["accountCreditRedeemShopify"];

export function withAction(Component: any) {
  class Actions extends React.Component<ActionsProps> {
    state = { textClicked: false, lastAction: undefined };
    preventScreenTransition: boolean = false;

    _onSuccess: Function | null = null;
    _onFailure: Function | null = null;
    wrappedElement: any;
    wrappedNode: Element | Text | null;
    pendingSmsShareArgs: { [props: string]: string } | null = null; // Track pending SMS share for auto-retry

    constructor(props: ActionsProps) {
      super(props);
      // create a ref to store the textInput DOM element
      this.wrappedElement = React.createRef();
    }

    /**
     * True if an action requires a preload. This occurs when
     * the component allowing the action loads.
     */
    shouldPreload(): boolean {
      const preloadActions = [
        "smsShare",
        "facebookShare",
        "messengerShare",
        "linkedInShare",
        "mailtoShare",
        "nativeMobileShare",
        "twitterShare",
        "whatsAppShare",
        "googleWalletLink",
        "appleWalletDownload",
      ];
      const actionName = get(this.props, "action.name", "");

      return preloadActions.indexOf(actionName) > -1;
    }

    /**
     * Trigger the preload of the action. An action with a preload
     * needs to be defined with a suffix "Preload":
     * i.e smsShare -> smsSharePreload
     */
    preload(): void {
      this.runAction(
        this.props.action.name + "Preload",
        this.props.action.args
      );
    }

    /**
     * Trigger an action to be run when the component is first loaded.
     */
    runActionOnLoad(): void {
      const { actionOnLoad } = this.props;

      if (get(this.props, "actionOnLoad.name")) {
        this.runAction(actionOnLoad.name, actionOnLoad.args);
      }
    }

    /**
     * In order to add a click action on the component that HOC is
     * wrapping we use vanilla javascript addEventListener with
     * `ReactDOM.findDOMNode`. This also allow us to trigger a
     * sms window in iPhone, otherwise blocked when we try to trigger
     * it with a synthetic react event
     */
    componentDidMount() {
      this.wrappedNode = ReactDOM.findDOMNode(this.wrappedElement.current);
      if (this.wrappedNode) {
        this.wrappedNode.addEventListener("click", this.onClick);
      }

      if (isEditMode()) {
        return;
      }

      if (this.shouldPreload()) {
        this.preload();
      }

      if (get(this.props, "actionOnLoad.name")) {
        this.runActionOnLoad();
      }
    }

    // Just removing the event attached to the wrapped component
    componentWillUnmount() {
      if (this.wrappedNode) {
        this.wrappedNode.removeEventListener("click", this.onClick);
      }
    }

    // tslint:disable-next-line: cyclomatic-complexity
    onClick = async (event: any) => {
      const { textClicked } = this.state;
      const { action, widgetEventTrack } = this.props;
      const ignoreOnClickedActions = [
        "facebookShare",
        "messengerShare",
        "linkedInShare",
        "mailtoShare",
        "nativeMobileShare",
        "twitterShare",
        "whatsAppShare",
        "redirect",
        "googleWalletLink",
        "appleWalletDownload",
        "smsShare",
        "copyText",
      ];

      // Actions that are broadcastable to the merchant. Add here as needed.
      const broadcastableActions = ["smsShare", "copyText"];

      if (isEditMode()) {
        return;
      }

      if (
        action &&
        (!ignoreOnClickedActions.includes(action.name) ||
          broadcastableActions.includes(action.name))
      ) {
        const values = pickBy(
          mapValues(action.args, this.interpolate),
          (value: string) => value !== ""
        );

        widgetEventTrack({
          componentName: this.props.config[this.props.configName].label,
          actionName: action.name,
          isFormSubmitButton: String(this.props.submitButton),
          isFormValid: String(!this.validateFields(false)),
          ...values,
          action: "action_clicked",
          type: "widget_event",
        });
      }

      // if there are errors on the page and the action is a submit action then we don't proceed
      if (this.props.submitButton) {
        await this.props.clearWidgetFormErrors();
        const hasError = this.validateFields(true);

        if (hasError) {
          event.preventDefault();
          event.stopPropagation();

          return;
        }
      }

      // if the text was already clicked we don't do anything
      if (textClicked) {
        return;
      }

      if (!action || !action.name) {
        return;
      }

      this.runAction(action.name, action.args);

      if (!ignoreOnClickedActions.includes(action.name)) {
        this.setState({ textClicked: true });
      }

      if (["copyText", "redirect"].includes(action.name)) {
        this.goToSuccessScreen();
      }
    };

    validateFields = (setFormError: boolean): boolean => {
      const { data, config, screenName, setWidgetFormError } = this.props;
      let hasError: boolean = false;

      const children = getAllChildren(config, screenName);
      for (const id of children) {
        const { validators, name, component } = get(config, `[${id}]`, {});

        if (validators) {
          let inputValue = get(data, name);

          // because we need to validate multiple items for this component we split the string into an array
          if (component === "EmailTagInput" && inputValue) {
            inputValue = inputValue.replace(/\s/g, "").split(",");
          }

          const errorMessage = validate(validators, inputValue, data);
          if (setFormError) {
            setWidgetFormError(name, errorMessage);
          }

          if (errorMessage) {
            hasError = true;
          }
        }
      }

      return hasError;
    };

    runAction = (name: string, args?: { [props: string]: string }) => {
      const actions = {
        ...pick(this.props, availablePropActions),
        accountCreditRedeemShopify: this.accountCreditRedeem(true),
        facebookShare: this.facebookShare,
        instagramShare: this.instagramShare,
        messengerShare: this.messengerShare,
        nativeMobileShare: this.nativeMobileShare,
        linkedInShare: this.linkedInShare,
        twitterShare: this.twitterShare,
        transitionScreen: this.goToSuccessScreen,
        smsShare: this.smsShare,
        smsSharePreload: this.smsSharePreload,
        messengerSharePreload: this.messengerSharePreload,
        linkedInSharePreload: this.linkedInSharePreload,
        twitterSharePreload: this.twitterSharePreload,
        facebookSharePreload: this.facebookSharePreload,
        nativeMobileSharePreload: this.nativeMobileSharePreload,
        whatsAppShare: this.whatsAppShare,
        whatsAppSharePreload: this.whatsAppSharePreload,
        mailtoSharePreload: this.mailtoSharePreload,
        mailtoShare: this.mailtoShare,
        productShare: this.productShare,
        googleWalletLink: this.googleWalletLink,
        googleWalletLinkPreload: this.googleWalletLinkPreload,
        appleWalletDownload: this.appleWalletDownload,
        appleWalletDownloadPreload: this.appleWalletDownloadPreload,
        redirect,
        copyText,
      };

      this.setState({ lastAction: name });

      // tslint:disable-next-line: no-any
      const defaultMethod = () =>
        console.warn(`the action ${name} does not exist`, { name, args });

      const method = get(actions, name) || defaultMethod;
      const values = pickBy(
        mapValues(args, this.interpolate),
        (value: string) => value !== ""
      );

      // disableWidgetConfig requires a different payload
      if (name === "disableWidgetConfig") {
        return method(this.props.widgetId);
      }

      const data = get(this.props, "data", {});

      const optionalParams = pick(data, [
        "mobileNumber",
        "subscribe",
        "subscribeSms",
      ]);

      // always add merchantId, campaignId and widgetId to actions
      const actionParams = {
        ...optionalParams,
        ...values,
        widgetConfigId: this.props.widgetId,
        merchantId: this.props.merchantId,
        campaignId: this.props.campaignId,
        widgetId: this.props.widgetId,
      };

      if (get(data, "customer.id")) {
        // omit subscribe info from actionParams if customer.id is defined.
        // This implies that the email gate was skipped, which means the user
        // did not get a chance to uncheck the opt-in button.
        // These values can come from the optional params or form values, so sanitize both.
        delete actionParams.mobileNumber;
        delete actionParams.subscribe;
        delete actionParams.subscribeSms;
      }

      // because the value for recipients is a string we want to get the individual emails
      const recipients = values.recipients;
      if (name === "emailShare" && recipients && recipients !== "") {
        return method({
          ...actionParams,
          recipients: getEmailsFromString(recipients),
        });
      }

      if (name === "redirect") {
        return method({
          ...actionParams,
          couponObject: get(this.props, "coupon", ""),
          originalUrl: get(args, "url", ""),
        });
      }

      return method(actionParams);
    };

    /**
     * Local interpolation method that uses the widget data and form
     * to compose an interpolated string
     */
    interpolate = (value: string): string => {
      const configShareData = get(this.props, ["config", "data", "share"], {});
      const { advocate, influencer } = extractInfluencerAndAdvocate(
        configShareData,
        this.props.referral
      );

      const state = {
        ...this.props.data,
        // not very elegant, we should maybe add the referral to `data`
        referral: this.props.referral,
        share: this.props.share,
        coupon: get(this.props, "coupon.coupon", ""),
        advocate,
        influencer,
      };

      const defaultToBlank = !isEditMode();

      // this is not a recursive call, interpolate !== this.interpolate
      return interpolate(value, state, defaultToBlank);
    };

    addLoadingClassToElement = () => {
      if (this.wrappedNode) {
        (this.wrappedNode as any).classList.add("loading");
      }
    };

    removeLoadingClassFromElement = () => {
      if (this.wrappedNode) {
        (this.wrappedNode as any).classList.remove("loading");
      }
    };

    pollForReward = ({
      amount,
      currency,
      isUsingShopify,
    }: {
      amount: string;
      currency: string;
      isUsingShopify: boolean;
    }) => {
      const transactionId = get(
        this.props,
        "accountCreditRedeem.transactionId"
      );

      if (transactionId) {
        const parentWindowOrigin = get(this.props, "data.url.origin");
        const parentWindowPathname = get(this.props, "data.url.pathname");

        this.setState({ textClicked: true });
        this.props.pollForReward({
          transactionId,
        });

        this.onSuccess(() => {
          const code = get(this.props, "accountCreditRedeem.code");
          copyText({ text: code });

          // by sending the user to this URL we can apply the discount to their cart automatically
          // the merchant SDK will then display a success modal
          if (isUsingShopify) {
            // because the params we add get reordered alphabetically we use afbuy to make sure it comes first
            // shopify does funky things with the redirect
            window.top.location.href = `${parentWindowOrigin}/discount/${code}?redirect=${parentWindowPathname}/?&afbuy_redirect=true&fbuy_ledger_redeem=true&fbuy_ledger_amount=${amount}&fbuy_ledger_currency=${currency}&fbuy_ledger_code=${code}`;
          }
        });
      }
    };

    accountCreditRedeem =
      (isUsingShopify: boolean) => (args: { [props: string]: string }) => {
        const redeemAmount = get(this.props, "data.redeemAmount");
        const redeemCurrency = get(args, "currency", "USD");
        const redeemType = get(
          args,
          "redeemType"
        ) as AccountCreditRedeemActionParam;

        if (redeemType && redeemAmount) {
          this.props.accountCreditRedeemAction({
            action: redeemType,
            amount: Number(redeemAmount),
            currency: redeemCurrency,
          });

          this.onSuccess(() => {
            this.pollForReward({
              amount: redeemAmount,
              currency: redeemCurrency,
              isUsingShopify,
            });
          });
        }
      };

    nativeMobileSharePreload = (args: any) => {
      // pull the native mobile share if it does not exist yet
      if (!this.props.share || !this.props.share.nativeMobile) {
        this.props.getShare({
          ...args,
          message: "",
          short: true,
          channel: "nativeMobile",
        });
      }
    };

    nativeMobileShare = (args: { [props: string]: string }) => {
      if (
        this.props.share &&
        this.props.share.nativeMobile &&
        (navigator as any).share
      ) {
        (navigator as any)
          .share({
            url: this.props.share.nativeMobile.shortUrl,
            text: args.message,
          })
          .then((res: any) => {
            this.goToSuccessScreen();
          })
          .catch((e: any) => {
            return;
          });
      }
    };

    facebookSharePreload = (args: any) => {
      // pull the facebook share if it does not exist yet
      if (!this.props.share || !this.props.share.facebook) {
        this.props.getShare({
          ...args,
          message: "",
          short: true,
          channel: "facebook",
        });
      }
    };

    facebookShare = (args: { [props: string]: string }) => {
      const isFacebookDisabled = get(
        this.props,
        "config.settings.disabledWidgetFeatures.facebookSharing",
        false
      );
      const facebookAppId = get(
        this.props,
        "config.settings.facebookAppId",
        false
      );

      if (isFacebookDisabled && this.props.share && this.props.share.facebook) {
        window.open(
          `${WIDGET_URL}facebookDisabled.html?link=${this.props.share.facebook.shortUrl}`,
          "",
          "toolbar=no,location=no,directories=no,status=no,menubar=no,scrollbars=yes,resizable=yes,width=600,height=400,top=" +
            (screen.height / 2 - 200) +
            ",left=" +
            (screen.width / 2 - 300)
        );

        return;
      }

      if (this.props.share && this.props.share.facebook) {
        const facebookWindow = window.open(
          `https://www.facebook.com/dialog/share?app_id=${facebookAppId}&display=popup&href=${
            this.props.share.facebook.shortUrl
          }&quote=${encodeURIComponent(this.interpolate(args.message))}`,
          "",
          "toolbar=no,location=no,directories=no,status=no,menubar=no,scrollbars=yes,resizable=yes,width=600,height=400,top=" +
            (screen.height / 2 - 200) +
            ",left=" +
            (screen.width / 2 - 300)
        );

        const facebookWindowTimer = setInterval(() => {
          if (facebookWindow && facebookWindow.closed) {
            clearInterval(facebookWindowTimer);
            this.goToSuccessScreen();
          }
        }, 100);
      }
    };

    messengerSharePreload = (args: any) => {
      // pull the messenger share if it does not exist yet
      if (!this.props.share || !this.props.share.messenger) {
        this.props.getShare({
          ...args,
          message: "",
          short: true,
          channel: "messenger",
        });
      }
    };

    messengerShare = (args: { [props: string]: string }) => {
      const isMessengerDisabled = get(
        this.props,
        "config.settings.disabledWidgetFeatures.messengerSharing",
        false
      );
      const facebookAppId = get(
        this.props,
        "config.settings.facebookAppId",
        false
      );

      if (
        isMessengerDisabled &&
        this.props.share &&
        this.props.share.messenger
      ) {
        (window as any).open(
          `${WIDGET_URL}messengerDisabled.html?link=${get(
            this.props,
            "share.messenger.shortUrl",
            ""
          )}`,
          "",
          "toolbar=no,location=no,directories=no,status=no,menubar=no,scrollbars=yes,resizable=yes,width=600,height=400,top=" +
            (screen.height / 2 - 200) +
            ",left=" +
            (screen.width / 2 - 300)
        );

        return;
      }

      if (this.props.share && this.props.share.messenger) {
        // messenger share through javascript sdk does not work on mobile
        if (/Mobi|Android/i.test(navigator.userAgent)) {
          window.open(
            `fb-messenger://share/?link=${encodeURIComponent(
              this.props.share.messenger.shortUrl
            )}&app_id=${facebookAppId}`
          );
        } else {
          const messengerWindow = (window as any).open(
            `https://www.facebook.com/dialog/send?app_id=${facebookAppId}&display=popup&link=${this.props.share.messenger.shortUrl}&redirect_uri=http://widget.fbot.me/fbShareRedirect.html`,
            "",
            "toolbar=no,location=no,directories=no,status=no,menubar=no,scrollbars=yes,resizable=yes,width=600,height=200,top=" +
              (screen.height / 2 - 100) +
              ",left=" +
              (screen.width / 2 - 300)
          );

          const messengerWindowTimer = setInterval(() => {
            if (messengerWindow && messengerWindow.closed) {
              clearInterval(messengerWindowTimer);
              this.goToSuccessScreen();
            }
          }, 100);
        }
      }
    };

    twitterSharePreload = (args: any) => {
      // pull the twitter share if it does not exist yet
      if (!this.props.share || !this.props.share.twitter) {
        this.props.getShare({
          ...args,
          message: "",
          short: true,
          channel: "twitter",
        });
      }
    };

    twitterShare = (args: { [props: string]: string }) => {
      if (this.props.share && this.props.share.twitter) {
        window.open(
          `https://twitter.com/intent/tweet?url=${
            this.props.share.twitter.shortUrl
          }&text=${encodeURIComponent(this.interpolate(args.message))}`,
          "",
          "toolbar=no,location=no,directories=no,status=no,menubar=no,scrollbars=yes,resizable=yes,width=600,height=200,top=" +
            (screen.height / 2 - 100) +
            ",left=" +
            (screen.width / 2 - 300)
        );
        this.goToSuccessScreen();
      }
    };

    linkedInSharePreload = (args: any) => {
      // pull the linkedIn share if it does not exist yet
      if (!this.props.share || !this.props.share.linkedIn) {
        this.props.getShare({
          ...args,
          short: true,
          channel: "linkedIn",
        });
      }
    };

    linkedInShare = (args: { [props: string]: string }) => {
      if (this.props.share && this.props.share.linkedIn) {
        window.open(
          `https://www.linkedin.com/shareArticle?mini=true&url=${
            this.props.share.linkedIn.shortUrl
          }&title=&summary=${encodeURIComponent(
            this.interpolate(args.message)
          )}&source=friendbuy`,
          "",
          "toolbar=no,location=no,directories=no,status=no,menubar=no,scrollbars=yes,resizable=yes,width=600,height=200,top=" +
            (screen.height / 2 - 100) +
            ",left=" +
            (screen.width / 2 - 300)
        );
        this.goToSuccessScreen();
      }
    };

    smsSharePreload = (args: any) => {
      // SMS share is now created at click time when consent is known
      // to ensure smsConsentParam is included in the POST body.
    };

    /**
     * Helper method to check SMS consent from data or DOM
     */
    checkSmsConsent = (): boolean => {
      // Consent already recorded in data (checkbox was checked).
      // The Checkbox container's onChange always calls setUserInput(name, "yes"|"no")
      // synchronously, so data.smsConsent is reliably "yes" when the checkbox is checked.
      const consentFromData =
        get(this.props, "data.smsConsent") ||
        get(this.props, "data.iKnowThisPerson");
      if (consentFromData === "yes" || consentFromData === true) {
        return true;
      }

      // Check if the smsConsent checkbox component exists in the current screen layout.
      // If config/screenName is unavailable, safely allow the share (return true).
      // If the checkbox is not in the layout, bypass consent validation (return true).
      // If the checkbox is in the layout but unchecked, block the share (return false).
      const { config, screenName } = this.props;
      if (!config || !screenName) {
        return true;
      }

      const children = getAllChildren(config, screenName);
      const hasCheckbox = children.some((id) => {
        const componentName = get(config, `[${id}].name`);

        return componentName === "smsConsent" || componentName === "iKnowThisPerson";
      });

      return !hasCheckbox;
    };

    /**
     * Helper method to handle missing SMS consent
     */
    handleMissingSmsConsent = (): void => {
      if (this.props.setWidgetFormError) {
        this.props.setWidgetFormError(
          "smsConsent",
          "Please confirm that you have a personal relationship with the recipient(s) before sending SMS."
        );
      }

      if (this.props.trackEvent) {
        this.props.trackEvent({
          action: "sms_share_blocked_missing_confirmation",
          type: "widget_event",
        });
      }
    };

    /**
     * Helper method to format SMS message
     */
    formatSmsMessage = (
      message: string,
      shortUrl: string,
      isIOS: boolean
    ): string => {
      let formattedMessage = this.interpolate(message);

      // truncate long messages
      if (formattedMessage.length > 159 - shortUrl.length) {
        formattedMessage =
          formattedMessage.slice(0, 156 - shortUrl.length) + "...";
      }

      if (!isIOS && formattedMessage && formattedMessage.length > 0) {
        // Double encode & symbols for android to avoid message truncation
        formattedMessage = formattedMessage.replace(/&/g, "%26");
      }

      return formattedMessage;
    };

    smsShare = (args: { [props: string]: string }) => {
      // Validate consent first
      const hasConsent = this.checkSmsConsent();

      if (!hasConsent) {
        this.handleMissingSmsConsent();

        return;
      }

      // Clear any errors
      if (this.props.setWidgetFormError) {
        this.props.setWidgetFormError("smsConsent", null);
      }

      // Create share if it doesn't exist (with consent in POST body)
      if (!this.props.share || !this.props.share.sms) {
        // Save args for auto-retry when share becomes available
        this.pendingSmsShareArgs = args;

        this.props.getShare({
          ...args,
          merchantId: this.props.merchantId,
          campaignId: this.props.campaignId,
          widgetConfigId: this.props.widgetConfigId,
          email: args.email || "",
          message: args.message || "",
          short: true,
          channel: "sms",
          smsConsent: "yes",
        });

        return;
      }

      this.pendingSmsShareArgs = null;

      const ua = navigator.userAgent.toLowerCase();
      const isIOS = ua.indexOf("iphone") > -1 || ua.indexOf("ipad") > -1;
      const shortUrl = this.props.share.sms.shortUrl;
      const message = this.formatSmsMessage(args.message, shortUrl, isIOS);
      const body = encodeURIComponent(`${message} ${shortUrl}`);

      // currently, using the `sms:` protocol in combination w/ `window.location` has
      // compatibility issues across iOS and Android.
      // TODO: we may want to consider switching over to use the `navigator.share` API,
      // but the UI for that is more like a general mobile share than direct to SMS.
      const windowLocation = isIOS ? window.top.location : window.location;
      windowLocation.href = isIOS
        ? `sms:&body=${body}` // Apple iOS format
        : `sms:?body=${body}`; // Android format
    };

    whatsAppSharePreload = (args: any) => {
      // pull the whatsApp share if it does not exist yet
      if (!this.props.share || !this.props.share.whatsApp) {
        this.props.getShare({
          ...args,
          short: true,
          channel: "whatsApp",
        });
      }
    };

    whatsAppShare = (args: { [props: string]: string }) => {
      if (this.props.share && this.props.share.whatsApp) {
        const { shortUrl } = this.props.share.whatsApp;
        const message = this.interpolate(args.message);
        window.open(
          `https://wa.me/?text=${encodeURIComponent(`${message} ${shortUrl}`)}`
        );
        this.goToSuccessScreen();
      }
    };

    mailtoSharePreload = (args: any) => {
      // pull the mailto share if it does not exist yet
      if (!this.props.share || !this.props.share.mailto) {
        this.props.getShare({
          ...args,
          short: true,
          channel: "mailto",
        });
      }
    };

    mailtoShare = (args: { [props: string]: string }) => {
      if (this.props.share && this.props.share.mailto) {
        const { shortUrl } = this.props.share.mailto;
        const message = this.interpolate(args.message);

        const body = encodeURIComponent(`${message} ${shortUrl}`);
        const subject = encodeURIComponent(this.interpolate(args.subject));

        window.open(`mailto:?body=${body}&subject=${subject}`);
        this.goToSuccessScreen();
      }
    };

    productShare = (args: { [props: string]: string }) => {
      const currentUrl = get(this.props, "data.url.current");
      const productParams = extractShopifyParams(currentUrl);
      const email = args.email || get(this.props, "data.customer.email", "");

      // Generate the productPurl share link
      this.props.getShare({
        merchantId: this.props.merchantId,
        campaignId: this.props.campaignId,
        widgetConfigId: this.props.widgetId,
        email,
        message: "",
        short: true,
        channel: PRODUCT_PURL_CHANNEL,
        destinationUrl: currentUrl,
        ...productParams,
        ...args,
      });

      // Navigate to success screen after share link is generated
      this.onSuccess(() => {
        this.goToSuccessScreen();
      });
    };

    googleWalletLinkPreload = (args: Record<string, string>) => {
      // pull the google wallet link if it does not exist yet
      if (!this.props.googleWallet || !this.props.googleWallet.link) {
        const componentConfig = this.props.config[this.props.configName] || {};
        const channel =
          (componentConfig as any).channel || GOOGLE_WALLET_CHANNEL;

        const productParams = PRODUCT_CHANNELS.includes(channel)
          ? extractShopifyParams(get(this.props, "data.url.current"))
          : {};

        this.props.getReferral({
          ...(args as GetReferralParams),
          merchantId: this.props.merchantId,
          campaignId: this.props.campaignId,
          widgetConfigId: this.props.widgetId,
          channel,
          ...productParams,
        });
      }
    };

    googleWalletLink = (args: Record<string, string>) => {
      if (this.props.googleWallet && this.props.googleWallet.link) {
        this.props.trackEvent({
          ...args,
          type: "widget_event",
          action: GOOGLE_WALLET_CLICK_EVENT_NAME,
          referralCode: this.props.googleWallet.code,
        });

        const googleWalletLink = this.props.googleWallet.link;
        window.open(googleWalletLink, "_blank");
      }
    };

    appleWalletDownloadPreload = (args: Record<string, string>) => {
      // pull the apple wallet link if it does not exist yet
      if (!this.props.appleWallet || !this.props.appleWallet.link) {
        // tslint:disable-next-line:no-any
        const componentConfig = this.props.config[this.props.configName] || {};
        const channel =
          (componentConfig as any).channel || APPLE_WALLET_CHANNEL;

        const productParams = PRODUCT_CHANNELS.includes(channel)
          ? extractShopifyParams(get(this.props, "data.url.current"))
          : {};

        this.props.getReferral({
          ...(args as GetReferralParams),
          merchantId: this.props.merchantId,
          campaignId: this.props.campaignId,
          widgetConfigId: this.props.widgetId,
          channel,
          ...productParams,
        });
      }
    };

    appleWalletDownload = (args: Record<string, string>) => {
      if (this.props.appleWallet && this.props.appleWallet.link) {
        // The actual pass download is handled by the component.
        // We just need to track the event here
        this.props.trackEvent({
          ...args,
          type: "widget_event",
          action: APPLE_WALLET_CLICK_EVENT_NAME,
          referralCode: this.props.appleWallet.code,
        });
      }
    };

    instagramShare = (args: { [props: string]: string }) => {
      this.props.getShare({
        merchantId: args.merchantId,
        campaignId: args.campaignId,
        widgetConfigId: args.widgetId,
        widgetId: args.widgetId,
        email: args.email,
        message: "",
        short: true,
        channel: "instagram",
      });

      this.onSuccess(() => {
        const { shortUrl } = this.props.share.instagram;
        copyText({ text: shortUrl });
        redirect({
          url: "https://instagram.com",
        });
      });
    };

    preventNextScreenTransition() {
      this.preventScreenTransition = true;
    }

    // tslint:disable:cyclomatic-complexity
    componentDidUpdate(prevProps: ActionsProps) {
      // Check if SMS share just became available
      if (
        this.pendingSmsShareArgs &&
        this.props.share &&
        this.props.share.sms &&
        (!prevProps.share || !prevProps.share.sms)
      ) {
        // Share is now ready, retry SMS share.
        // null the pending args before calling smsShare to prevent re-entry
        // if componentDidUpdate fires again during the call.
        const args = this.pendingSmsShareArgs;
        this.pendingSmsShareArgs = null;
        this.smsShare(args);
      }

      const { requestStatus } = this.props;

      // Handle failure or malformed success to prevent infinite/stale retry loops
      const transitionedToFailure =
        prevProps.requestStatus !== "FAILURE" && requestStatus === "FAILURE";
      const transitionedToSuccess =
        prevProps.requestStatus !== "SUCCESS" && requestStatus === "SUCCESS";
      const smsStillMissing = !this.props.share || !this.props.share.sms;

      if (
        this.pendingSmsShareArgs &&
        (transitionedToFailure || (transitionedToSuccess && smsStillMissing))
      ) {
        this.pendingSmsShareArgs = null;
      }

      const indexScreen = this.props.config.index as IndexScreen;
      const { actionOnLoad } = indexScreen;

      // new customer email from authentication should result
      // in action on load being re-run
      const shouldRunOnLoadAction =
        customerEmailChanged(prevProps.data, this.props.data) &&
        customerIsAuthenticated(this.props.data);

      // actionOnLoad specifically in if condition as oppposed to shouldrUnOnLoadAction
      // expression so that compiler doesn't yell
      if (shouldRunOnLoadAction && actionOnLoad) {
        this.runAction(
          actionOnLoad.name as string,
          actionOnLoad.args as ActionOnLoadArgs
        );
      }

      if (
        prevProps.requestStatus !== "REQUESTING" &&
        requestStatus === "REQUESTING"
      ) {
        this.goToLoadingScreen();

        if (this.state.textClicked) {
          this.addLoadingClassToElement();
        }
      }

      if (
        prevProps.requestStatus !== "SUCCESS" &&
        requestStatus === "SUCCESS" &&
        this.state.textClicked
      ) {
        this.removeLoadingClassFromElement();
        this.setState({ textClicked: false });

        if (this._onSuccess) {
          this._onSuccess();
          if (
            this.state.lastAction &&
            !asyncActions.includes(this.state.lastAction as unknown as string)
          ) {
            this._onSuccess = null;
          }
        }
        this._onFailure = null;

        this.goToSuccessScreen();
      }

      if (
        prevProps.requestStatus !== "FAILURE" &&
        requestStatus === "FAILURE" &&
        this.state.textClicked
      ) {
        this.removeLoadingClassFromElement();
        this.setState({ textClicked: false });

        if (this._onFailure) {
          this._onFailure();
          this._onFailure = null;
        }
        this._onSuccess = null;

        this.goToFailureScreen();
      }
    }

    addSuccessNotification = () => {
      const { action } = this.props;
      const successNotificationElement = document.getElementById(
        "widget-success-notification"
      );
      const successNotificationTextElement = document.getElementById(
        "widget-success-notification-text"
      );

      if (successNotificationTextElement && get(action, "notification.text")) {
        successNotificationTextElement.innerText = action.notification.text;
        successNotificationTextElement.classList.add(
          "Widget-success-inner-container-expanded"
        );
        setTimeout(
          () =>
            successNotificationTextElement.classList.remove(
              "Widget-success-inner-container-expanded"
            ),
          3000
        );
      }

      if (successNotificationElement) {
        successNotificationElement.classList.add(
          "Widget-success-container-expanded"
        );
        setTimeout(
          () =>
            successNotificationElement.classList.remove(
              "Widget-success-container-expanded"
            ),
          3000
        );
      }
    };

    /**
     * run a second action if available and if first action is successful
     */
    runSuccessAction = () => {
      const { config, successAction, screenName } = this.props;

      if (get(successAction, "action")) {
        const screenLabel = get(config, `${screenName}.label`);
        set(successAction.args, "screenLabel", screenLabel);
        set(successAction.args, "screenName", screenName);

        this.runAction(successAction.action, successAction.args);
      }
    };

    /**
     * set a callback to be triggered on an asynchronous success
     */
    onSuccess = (callback: Function, ...args: any) =>
      (this._onSuccess = () => callback(...args));

    /**
     * set a callback to be triggered on an asynchronous failure
     */
    onFailure = (callback: Function, ...args: any) =>
      (this._onFailure = () => callback(...args));

    goToLoadingScreen = () => {
      const { config, requestScreen, setScreenName } = this.props;
      if (requestScreen && config[requestScreen]) {
        setScreenName(requestScreen);
      }
    };

    goToSuccessScreen = () => {
      const { action, config, successScreen, setScreenName } = this.props;
      this.runSuccessAction();

      if (get(action, "notification.display")) {
        this.addSuccessNotification();
      }

      if (this.preventScreenTransition) {
        this.preventScreenTransition = false;

        return;
      }

      if (successScreen && config[successScreen]) {
        setScreenName(successScreen);
      }
    };

    goToFailureScreen = () => {
      const { config, failureScreen, setScreenName } = this.props;

      if (this.preventScreenTransition) {
        this.preventScreenTransition = false;

        return;
      }

      if (failureScreen && config[failureScreen]) {
        setScreenName(failureScreen);
      }
    };

    render() {
      return <Component ref={this.wrappedElement} {...this.props} />;
    }
  }

  function mapStateToProps(state: StoreState) {
    return {
      accountCreditRedeem: getAccountCreditRedeem(state),
      campaignId: getCampaignId(state),
      coupon: getCoupon(state),
      formErrors: getWidgetFormErrors(state),
      merchantId: getMerchantId(state),
      requestStatus: getReferralRequestStatus(state),
      widgetConfigId: getWidgetConfigId(state),
      widgetId: getWidgetId(state),
      data: getWidgetData(state),
      screenName: getScreenName(state),
    };
  }

  return connect(mapStateToProps, {
    accountCreditRedeemAction,
    setScreenName,
    setWidgetFormError,
    getLedgerContext,
    getPDPPointsTotal,
    getReferral,
    getShare,
    emailShare,
    widgetEventTrack,
    pollForReward,
    setUserInput,
    trackEvent,
    disableWidgetConfig,
    clearWidgetFormErrors,
    loyaltyOptIn,
  })(Actions);
}
